import React, { useEffect, useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import { FluentAvatar } from "../FluentUI/Fluent-Avatar";
import { List, ListItem } from '@fluentui/react-list-preview';
import { FluentSearch } from "../FluentUI/Fluent-Search";
import { FluentCheckbox } from "../FluentUI/Fluent-Checkbox";
import { FluentField } from "../FluentUI/Fluent-Field";
import DropdownProjectOwner from "./dropdown-project-owner";
import { useStyles } from "./select-styles";
import PropTypes from "prop-types";
import FluentUiIcon from "../../assets/fluent-ui-icon";
import { getAvatarColor, getGroupMembers, getInitials, sortProjectMembers } from "./select-member-helper";
import { highlightMatch } from "../../pages/new-dashboard/dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { unavailableReason } from "../TaskAssignMember/TaskAssignMemberHelper";

const MultipleOwnerSelectMemberProject = ({
  selectedOwners,
  data,
  saveMembers,
  saveOwners,
  user,
  isEdit,
  labels,
  disabled = false,
  canTransferOwner = true
}) => {
  const [userData, setUserData] = useState(data.allUsers);
  const [sortedUserData, setSortedUserData] = useState(data.allUsers);
  const [userFilter, setUserFilter] = useState("");
  const [selectedMembers, setSelectedMembers] = useState(data.Members);
  const [initialSelectedMembers, setInitialSelectedMembers] = useState([]);
  const [initialSelectedOwners, setInitialSelectedOwners] = useState([]);
  const classes = useStyles();

  useEffect(() => {
    const sorted = sortProjectMembers(userData, selectedOwners, selectedMembers);
    setSortedUserData(sorted);
  }, [userData, selectedOwners, selectedMembers]);

  useEffect(() => {
    if (isEdit) {
      setInitialSelectedMembers(data.Members);
      setInitialSelectedOwners(selectedOwners);
    }
  }, []);
  
  const canMemberEdit = () => {
   return (!initialSelectedMembers.some(member => 
      (!member.IsGroup && member.Id === user.userID) || 
      (member.IsGroup && member.Members?.some(m => m.UserId === user.userID))
    ) && isEdit) ? false : true;
  }

  const selectAllTypes = (e, toggle) => {
    if (!canMemberEdit()) return;
    if (toggle.checked) {
        const updatedSelection = [...userData];
        setSelectedMembers(updatedSelection);
        saveMembers(updatedSelection);
    } else {
        let updatedSelection;

        if (isEdit) {
            updatedSelection = userData.filter(member =>
                selectedOwners.some(owner =>
                    (!owner.IsGroup && owner.Id === member.Id) ||  
                    (owner.IsGroup && owner.Id === member.GroupId) 
                )
            );
        } else {
          updatedSelection = selectedOwners.length > 0 
              ? userData.filter(member =>
                  selectedOwners.some(owner => owner.Id === member.Id && owner.IsGroup === member.IsGroup)
              ) 
              : userData.filter(member => member.Id === user.userID);
        }      
        setSelectedMembers(updatedSelection);
        saveMembers(updatedSelection);
      }
};

  const selectedMember = (e, selectedItems) => {
    if (!canMemberEdit()) return;
    
    if (isEdit) {
      const isUserOwner = initialSelectedOwners.some((owner) => 
        (!owner.IsGroup && owner.Id === user.userID) || 
        (owner.IsGroup && owner.Members?.some(member => member.UserId === user.userID))
      );
      if (!isUserOwner) {
        const missingOwners = selectedOwners.filter(owner =>
          !selectedItems.some(item => item.Id === owner.Id && item.IsGroup === owner.IsGroup)
        );
        if (missingOwners.length > 0) {
          return;
        }
      }
    }
    
    if (!canTransferOwner && !isEdit) {
      const missingOwners = selectedOwners.filter(owner =>
        !selectedItems.some(item => item.Id === owner.Id && item.IsGroup === owner.IsGroup)
      );
      if (missingOwners.length > 0) {
        return;
      }
    }
    
      let updatedOwners = Array.isArray(selectedOwners) ? [...selectedOwners] : [];
      const missingOwners = updatedOwners.filter(owner =>
        !selectedItems.some(item => item.Id === owner.Id && item.IsGroup === owner.IsGroup)
      );
      if (missingOwners.length > 0 && updatedOwners.length === 1) {
        return; 
      }
      if (missingOwners.length > 0) {
        updatedOwners = updatedOwners.filter(owner => 
          selectedItems.some(item => item.Id === owner.Id && item.IsGroup === owner.IsGroup)
        );
  
        if (!updatedOwners.some(owner => owner.Id === user.userID)) {
          const currentUserAsOwner = selectedItems.find(x => x.Id === user.userID);
          if (currentUserAsOwner) {
            updatedOwners.push(currentUserAsOwner);
          }
        }
        saveOwners(updatedOwners);
      }
    setSelectedMembers(selectedItems);
    saveMembers(selectedItems);
  };
 
  const filterSearchedMembers = () => { 
    const selectAllListGroupsNew = userFilter?.toLowerCase() === "" ? (
      <div className={classes.selectAllContainer}>
        <FluentCheckbox
          onChange={selectAllTypes}
          label="Select All"
          required={false}
          componentStyle={classes.selectAllCheckbox}
          checked={
            selectedMembers.length === userData.length 
              ? true 
              : selectedMembers.length > 0 
                ? "mixed" 
                : false
          }
        />
      </div>
    ) : null;

    return (
      <>
        {selectAllListGroupsNew}
       
        <div>
          { 
            sortedUserData.filter(member => member.Name.toLowerCase().includes(userFilter.toLowerCase()) 
            || member.CompanyName?.toLowerCase().includes(userFilter.toLowerCase())
            || member.Role?.Name?.toLowerCase().includes(userFilter.toLowerCase())
            ).map((user) => {
            let fieldContent = user.GroupId
              ? `${user?.Name}`
              : `${user?.Name}${user?.Role?.Name ? ` (${user.Role.Name})` : ""}`;

            // Archived/moved member (TAD-5898): tooltip carries the reason instead.
            let tooltipContent = user?.Archived
              ? unavailableReason(user)
              : user.GroupId
                ? getGroupMembers(user)
                : `${fieldContent} - ${user?.CompanyName}`;

            return (
              <div key={`${user?.IsGroup ? "group" : "user"}-${user?.Id}`}>
                <ListItem
                  className={classes.memberItem}
                  value={user}
                  navigationMode="items"
                  aria-label={user.Name}
                  key={`${user?.IsGroup ? "group" : "user"}-${user?.Id}`}
                >
                  <Tooltip
                    content={tooltipContent}
                    id="tooltip-host"
                    positioning="above-start"

                  >
                    <div
                      className={user?.Archived ? classes.archivedMember : undefined}
                      style={{ marginLeft: "8px", display: "flex", alignItems: "center" }}
                    >
                      <FluentAvatar
                        size={30}
                        name={user?.Name}
                        initials={user?.Name ? getInitials(user?.Name) : "ZZ"}
                        color={getAvatarColor(user, selectedOwners)}
                        badge={
                          user?.IsGroup ? {
                            icon: (
                              <FluentUiIcon
                                name="PeopleFilled"
                                color={selectedOwners?.some(owner => owner.Id === user?.Id && owner?.IsGroup) ? "#370B10" : "#CD9EA5"}
                                size={10}
                              />
                            ),
                          } : null
                        }
                      />
                      <div
                        className={user?.Archived ? classes.archivedMemberText : undefined}
                        style={{ marginLeft: "8px", display: "flex", flexDirection: "column", fontSize: "14px", }}
                      >
                        <span>{highlightMatch(fieldContent, userFilter)}</span>
                        {user?.CompanyName && (
                          <span style={{ fontSize: "12px", color: "#6b6b6b" }}>
                            {highlightMatch(user.CompanyName, userFilter)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Tooltip>
                </ListItem>
              </div>
            );
          })}
        </div>
      </>
    );
  };
  return (
    <div>
      <FluentField
        label={`${labels?.ProjectSingular ?? "Assign"} Members`}
        required={true}
        size={"small"}
      />
        <FluentSearch
          componentStyle={classes.searchPanel}
          size={"small"}
          placeholder={"Search members/groups"}
          onChange={(e) => {
            setUserFilter(e.target.value || "");
          }}
        />
        <div className={classes.memberListContainer} id="memberlistgroup">
          <List
            selectionMode="multiselect"
            selectedItems={selectedMembers}
            onSelectionChange={(e, data) => {
              selectedMember(e, data.selectedItems)
            }}
          >
            {filterSearchedMembers()}
          </List>
        </div>

        {(!isEdit || 
          (isEdit && initialSelectedOwners.some((owner) => 
                    (!owner.IsGroup && owner.Id === user.userID) || 
                   (owner.IsGroup && owner.Group?.Members?.some(member => member.UserId === user.userID))
          ))) && <div id="ownerdropdown">
        <FluentField
          label="Owner"
          componentStyle={classes.projectOwnerEdit}
        >
          <DropdownProjectOwner
            componentStyle={classes.ownerDropDown}
            value={selectedOwners?.map(owner => `${owner.Id}-${owner.IsGroup}`) || []}
            isControlled={true}
            selectedOptions={selectedOwners?.map(owner => `${owner.Id}-${owner.IsGroup}`) || []}
            options={selectedMembers
            // Temporarily disabled groups for project owners - enable once group as task owner is developed
            // Pool-orphaned members stay display-only (TAD-5898) — not owner candidates.
            // Existing owners keep their option so the closed dropdown's value
            // and "{n} Members Selected" count still include them.
            .filter(member => !member.IsGroup &&
              (!member.IsOrphaned || selectedOwners?.some(owner => owner.Id === member.Id && !owner.IsGroup)))
            .map(member => ({
                key: `${member.Id}-${member.IsGroup}`,
                text: member.Name,
                value: `${member.Id}-${member.IsGroup}`, 
            }))}
            onChange={(e, data) => {
                let updatedOwners = Array.isArray(selectedOwners) ? [...selectedOwners] : [];
                if (data.selectedOwners) {
                  updatedOwners = [...data.selectedOwners];
                }             
                if (updatedOwners.length === 0 && selectedOwners.length === 1) {
                  return; 
                }
                saveOwners(updatedOwners);
            }}
            multiselect={true}
            disabled={disabled}
          />
        </FluentField>
      </div>}
    </div>
  );
};

MultipleOwnerSelectMemberProject.propTypes = {
  filter: PropTypes.string,
  selectedOwners: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number,
    IsGroup: PropTypes.bool,
    Name: PropTypes.string
  })),
  data: PropTypes.shape({
    allUsers: PropTypes.array,
    Members: PropTypes.array,
    selectedItems: PropTypes.array,
    selectedOwners: PropTypes.array
  }).isRequired,
  saveMembers: PropTypes.func.isRequired,
  saveOwners: PropTypes.func.isRequired,
  user: PropTypes.shape({
    Id: PropTypes.number,
    userID: PropTypes.number,
    CompanyName: PropTypes.string,
    Name: PropTypes.string,
    IsGroup: PropTypes.bool,
    GroupId: PropTypes.number,
    Role: PropTypes.shape({
      Name: PropTypes.string
    })
  }).isRequired,
  isEdit: PropTypes.bool,
  labels: PropTypes.shape({
      ProjectSingular: PropTypes.string,
      ProjectPlural: PropTypes.string,
      ProjectSingularPossessive: PropTypes.string,
      ProjectPluralPossessive: PropTypes.string,
      TaskSingular: PropTypes.string,
      TaskPlural: PropTypes.string,
      TaskSingularPossessive: PropTypes.string,
      TaskPluralPossessive: PropTypes.string,
      FormSingular: PropTypes.string,
      FormPlural: PropTypes.string,
      FormSingularPossessive: PropTypes.string,
      FormPluralPossessive: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  canTransferOwner: PropTypes.bool,
};

MultipleOwnerSelectMemberProject.defaultProps = {
  selectedOwners: [],
  isEdit: false,
  canTransferOwner: true,
};

export default MultipleOwnerSelectMemberProject;
